import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { motion } from 'framer-motion';
import { ArrowRight, MagnifyingGlass } from '@phosphor-icons/react';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchProducts(); }, [category]);

  const fetchProducts = async (retryCount = 0) => {
    try {
      const url = category === 'all' ? '/products' : `/products?category=${category}`;
      const response = await apiClient.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      if (retryCount < 2) {
        setTimeout(() => fetchProducts(retryCount + 1), 1500);
        return;
      }
    }
    finally { setLoading(false); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) { fetchProducts(); return; }
    setLoading(true);
    try {
      const response = await apiClient.get(`/products/search/${encodeURIComponent(searchQuery)}`);
      setProducts(response.data);
    } catch (error) { console.error('Search error:', error); }
    finally { setLoading(false); }
  };

  const handleSearchKeyPress = (e) => { if (e.key === 'Enter') handleSearch(); };

  if (loading) return <div className="min-h-screen pt-24 flex items-center justify-center"><p className="text-slate-400">Loading products...</p></div>;

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto px-8 md:px-16 lg:px-24">
        <div className="mb-12">
          <p className="text-sm uppercase tracking-[0.2em] text-slate-400 mb-4" data-testid="products-overline">Our Collection</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium leading-none text-foreground mb-6" data-testid="products-heading">Premium Custom Bottles</h1>
          <p className="text-base leading-relaxed text-slate-300 max-w-2xl" data-testid="products-description">Choose from our curated collection and customize each bottle to perfection.</p>
        </div>

        {/* Search Bar */}
        <div className="flex gap-3 mb-8 max-w-xl" data-testid="search-section">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyPress}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Button onClick={handleSearch} variant="outline" className="rounded-sm" data-testid="search-button">
            Search
          </Button>
          {searchQuery && (
            <Button onClick={() => { setSearchQuery(''); fetchProducts(); }} variant="ghost" className="rounded-sm text-slate-400" data-testid="clear-search">
              Clear
            </Button>
          )}
        </div>

        <Tabs value={category} onValueChange={(v) => { setCategory(v); setSearchQuery(''); }} className="mb-12" data-testid="category-tabs">
          <TabsList className="grid w-full max-w-md grid-cols-4">
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="corporate" data-testid="tab-corporate">Corporate</TabsTrigger>
            <TabsTrigger value="wedding" data-testid="tab-wedding">Wedding</TabsTrigger>
            <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <motion.div key={product.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-card border border-border overflow-hidden group" data-testid={`product-card-${index}`}>
              <div className="aspect-square overflow-hidden">
                <img src={product.image_url} alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-testid={`product-image-${index}`} />
              </div>
              <div className="p-6">
                <p className="text-xs uppercase tracking-wider text-slate-400 mb-2" data-testid={`product-category-${index}`}>{product.category}</p>
                <h3 className="text-xl font-normal mb-3 text-foreground" data-testid={`product-name-${index}`}>{product.name}</h3>
                <p className="text-sm text-slate-300 mb-4 line-clamp-2" data-testid={`product-description-${index}`}>{product.description}</p>
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-medium text-[#D4AF37]" data-testid={`product-price-${index}`}>${product.base_price.toFixed(2)}</p>
                  <Link to={`/products/${product.id}`}>
                    <Button variant="outline" size="sm" className="rounded-sm group" data-testid={`product-view-button-${index}`}>
                      Customize <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {products.length === 0 && <div className="text-center py-12" data-testid="no-products-message"><p className="text-slate-400">No products found.</p></div>}
      </div>
    </div>
  );
};

export default ProductsPage;
